'use client';

import type { PanelSize } from 'react-resizable-panels';

import { useMemo, useState } from 'react';

import { usePathname } from 'next/navigation';

import {
	ResizableLayout,
	type ResizableLayoutRightPanel,
} from '@/shared/components/resizable-layout';

import { APP_ROUTES } from '@/shared/constants/navigation';

import { FullPageAgent } from '../components/views/full-page-agent';
import { useAgentStore } from '../contexts/agent-provider';
import {
	FOCUSED_AI_LAYOUT_CONFIG,
	readFocusedAiLayoutSplitPercent,
	saveFocusedAiLayoutSplitPercent,
} from './focused-ai-layout-core';
import { useIsFocusedAiLayoutPage } from './use-focused-ai-layout';

type FocusedAiLayoutProps = {
	children: React.ReactNode;
	autoStartOnboarding?: boolean;
};

function getPageOpenSize(): string {
	return `${100 - readFocusedAiLayoutSplitPercent()}%`;
}

function savePageSize(size: PanelSize): void {
	saveFocusedAiLayoutSplitPercent(100 - size.asPercentage);
}

/** Persistent desktop layout: full-page chat plus an optional real application page beside it. */
export function FocusedAiLayout({ children, autoStartOnboarding = false }: FocusedAiLayoutProps) {
	const pathname = usePathname();
	const showPage = useIsFocusedAiLayoutPage();
	const resizeRequestId = useAgentStore((state) => state.focusedAiLayoutResizeRequestId);
	// On /ai the route's own children are the chat the main pane already renders — never duplicate it into the pane.
	const isAgentRoute = pathname === APP_ROUTES.AI_AGENT;
	// Frozen: `?onboarding=true` describes how the session began, so Conversation keeps one scroll key.
	const [initialAutoStartOnboarding] = useState(autoStartOnboarding);
	const rightPanel = useMemo<ResizableLayoutRightPanel>(
		() => ({
			node: isAgentRoute ? null : (
				<div className="h-full min-w-0 transform overflow-hidden">
					<div className="h-full min-w-0 overflow-y-auto overscroll-contain bg-background">
						{children}
					</div>
				</div>
			),
			open: showPage,
			getOpenSize: getPageOpenSize,
			openSizeRequestKey: resizeRequestId,
			minSize: `${FOCUSED_AI_LAYOUT_CONFIG.MIN_PANEL_WIDTH_PX}px`,
			mainMinSize: `${FOCUSED_AI_LAYOUT_CONFIG.MIN_PANEL_WIDTH_PX}px`,
			groupResizeBehavior: 'preserve-relative-size',
			handleClassName: 'bg-slate-950/10 dark:bg-white/10',
			onUserResize: savePageSize,
		}),
		[children, isAgentRoute, resizeRequestId, showPage]
	);

	return (
		<div className="flex h-full min-w-0 flex-1 overflow-hidden bg-slate-50 dark:bg-slate-950">
			<ResizableLayout rightPanel={rightPanel}>
				<FullPageAgent autoStartOnboarding={initialAutoStartOnboarding} />
			</ResizableLayout>
		</div>
	);
}
